import React, { useEffect } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useStats } from "@/api/stats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNumber } from "@/lib/utils";

function useChartHeight() {
  const [height, setHeight] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 640 ? 180 : 220
  );
  React.useEffect(() => {
    const handler = () => setHeight(window.innerWidth < 640 ? 180 : 220);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return height;
}

export function Stats() {
  const { data, isLoading } = useStats();
  const chartHeight = useChartHeight();

  useEffect(() => {
    document.title = "Stats — Beacon";
    return () => { document.title = "Beacon"; };
  }, []);

  if (isLoading || !data) {
    return <div className="h-64 animate-pulse rounded-lg bg-muted/40" />;
  }

  const matched = data.status_counts["matched"] ?? 0;
  const buckets = Object.entries(data.confidence_buckets).map(([label, count]) => ({ label, count }));
  const perSource = data.per_source.map((s) => ({ ...s }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Stats</h1>
        <p className="mt-1 text-sm text-muted-foreground">Evaluation funnel + LLM self-confidence.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Listings seen" value={formatNumber(data.total_listings)} />
        <Tile label="Matched" value={formatNumber(matched)} accent />
        <Tile label="Avg fit" value={`${data.avg_fit}%`} />
        <Tile label="Avg LLM confidence" value={`${data.avg_confidence}%`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>By source</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={perSource}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="source"
                  interval={0}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  angle={-30}
                  textAnchor="end"
                  height={45}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                <Bar dataKey="total" name="Seen" fill="hsl(var(--muted-foreground) / 0.6)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="matched" name="Matched" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>LLM self-confidence distribution</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Across {formatNumber(data.evaluations_count)} evaluations.
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {buckets.map((_, i) => (
                    <Cell key={i} fill="hsl(var(--primary))" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Confidence vs. your feedback</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              How the LLM's confidence stacks against what you actually thought.
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                <FeedbackRow
                  label="👍 rated good"
                  count={data.feedback.up_count}
                  avg={data.feedback.avg_conf_up}
                />
                <FeedbackRow
                  label="👎 not for me"
                  count={data.feedback.down_count}
                  avg={data.feedback.avg_conf_down}
                />
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Token usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Input <span className="font-mono text-foreground">{formatNumber(data.total_input_tokens)}</span>
              {" · "}
              Output <span className="font-mono text-foreground">{formatNumber(data.total_output_tokens)}</span>
            </p>
            <p className="text-muted-foreground">
              Approx cost <span className="font-mono text-foreground">${data.approx_cost.toFixed(4)}</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  color: "hsl(var(--popover-foreground))",
  fontSize: "12px",
};

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Card className={accent ? "border-primary/40 shadow-beam-sm" : ""}>
      <CardContent className="pt-6">
        <p className="font-mono text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
        <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

function FeedbackRow({ label, count, avg }: { label: string; count: number; avg: number | null }) {
  return (
    <tr className="border-t border-border first:border-t-0">
      <td className="py-2 text-foreground">{label}</td>
      <td className="py-2 text-right font-mono text-muted-foreground">{count}</td>
      <td className="py-2 pl-4 text-right font-mono text-muted-foreground">
        {avg !== null ? `avg ${avg}%` : "—"}
      </td>
    </tr>
  );
}
